/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo, useState, type ReactNode } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import StatusWorkflowDangerIcon from '@atlaskit/icon-lab/core/status-workflow-danger';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import Lozenge from '@atlaskit/lozenge/new';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type ConnectorInfo } from '../use3pConnectorResources';

const messages = defineMessages({
	connect: {
		id: 'confluence-account-setup.third-party-connection-item.connect',
		defaultMessage: 'Connect',
		description: 'Label for the button to connect to a third party service',
	},
	tryAgain: {
		id: 'confluence-account-setup.third-party-connection-item.try-again',
		defaultMessage: 'Try again',
		description:
			'Label for the button to retry connecting to a third party service after a failure',
	},
	failedToConnect: {
		id: 'confluence-account-setup.third-party-connection-item.failed-to-connect',
		defaultMessage: 'Failed to connect',
		description: 'Error message shown when connecting to a third party service fails',
	},
	connected: {
		id: 'confluence-account-setup.third-party-connection-item.connected',
		defaultMessage: 'Connected',
		description:
			'Success lozenge label shown when a third party service has been successfully connected',
	},
});

const styles = cssMap({
	errorRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	marginAuto: {
		marginLeft: 'auto',
	},
});

type ConnectionStatus = 'idle' | 'loading' | 'success' | 'error';

export type ConnectorInfoProps = {
	connectorInfo: ConnectorInfo;
	isPrimaryButton?: boolean;
	onConnected?: () => void;
};

export type LegacyThirdPartyConnectorProps = {
	icon: ReactNode;
	isPrimaryButton?: boolean;
	name: string;
	onConnect: () => Promise<void>;
	onConnected?: () => void;
};

export type ThirdPartyConnectorProps = ConnectorInfoProps | LegacyThirdPartyConnectorProps;

const isConnectorInfoProps = (props: ThirdPartyConnectorProps): props is ConnectorInfoProps =>
	'connectorInfo' in props;

export const ThirdPartyConnector = (props: ThirdPartyConnectorProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [status, setStatus] = useState<ConnectionStatus>('idle');

	const isConnectorInfo = isConnectorInfoProps(props);
	const { isPrimaryButton = false, onConnected } = props;
	const iconElement = isConnectorInfo
		? props.connectorInfo.icon?.({
				label: props.connectorInfo.displayName,
				size: 'medium',
			})
		: props.icon;
	const displayName = isConnectorInfo ? props.connectorInfo.displayName : props.name;
	const connectButtonStyle = useMemo(
		() =>
			status === 'error' || isPrimaryButton
				? undefined
				: {
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
						'--ds-background-brand-bold': token('color.background.success.bold'),
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
						'--ds-background-brand-bold-hovered': token('color.background.success.bold.hovered'),
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
						'--ds-background-brand-bold-pressed': token('color.background.success.bold.pressed'),
						backgroundColor: token('color.background.success.bold'),
						color: token('color.text.inverse'),
					},
		[isPrimaryButton, status],
	);

	const handleConnect = async () => {
		setStatus('loading');
		try {
			if (isConnectorInfo) {
				const authUrl = props.connectorInfo.authUrl;
				if (!authUrl) {
					throw new Error('No authUrl');
				}

				const width = 600;
				const height = 800;
				const left = window.screenX + (window.outerWidth - width) / 2;
				const top = window.screenY + (window.outerHeight - height) / 2;
				const features = `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes,scrollbars=yes`;
				await auth(authUrl, features);
			} else {
				await props.onConnect();
			}
			setStatus('success');
			onConnected?.();
		} catch (error) {
			setStatus('error');
		}
	};

	return (
		<Flex alignItems="center" gap="space.150">
			<Flex alignItems="center" gap="space.150">
				{iconElement}
				<Text weight="medium">{displayName}</Text>
			</Flex>
			<Box xcss={cx(styles.marginAuto)}>
				{status === 'success' ? (
					<Lozenge appearance="success" iconBefore={CheckMarkIcon}>
						{formatMessage(messages.connected)}
					</Lozenge>
				) : (
					<Flex alignItems="center" gap="space.100">
						{status === 'error' && (
							<Box xcss={cx(styles.errorRow)}>
								<StatusWorkflowDangerIcon
									label=""
									color={token('color.icon.danger')}
									size="small"
								/>
								<Text size="small" color="color.text.danger">
									{formatMessage(messages.failedToConnect)}
								</Text>
							</Box>
						)}
						<Button
							appearance={isPrimaryButton ? 'primary' : 'default'}
							isLoading={status === 'loading'}
							onClick={handleConnect}
							// @ts-expect-error UNSAFE_style is intentionally used here as the ADS escape hatch for theme-aware one-off button styling.
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
							UNSAFE_style={connectButtonStyle}
						>
							{status === 'error'
								? formatMessage(messages.tryAgain)
								: formatMessage(messages.connect)}
						</Button>
					</Flex>
				)}
			</Box>
		</Flex>
	);
};
